import { GridsterItemConfig } from 'angular-gridster2';
import {
  DashboardDTO,
  DashboardSettingsDTO,
  DashboardWidgetDTO,
  WidgetSettingsDTO
} from '../../dto/dashboard/dashboard.dto';
import { WidgetData, WidgetSettings } from '../../components/duna/duna-dashboard/duna-dashboard.component';

export class Dashboard {
  id: string;
  name: string;
  description?: string;
  ownerId: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
  widgets: DashboardWidget[];
  settings?: DashboardSettings;

  constructor(data: Partial<Dashboard> = {}) {
    this.id = data.id || this.generateId();
    this.name = data.name || 'Dashboard';
    this.description = data.description;
    this.ownerId = data.ownerId || 'system';
    this.isDefault = data.isDefault || false;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
    this.widgets = data.widgets || [];
    this.settings = data.settings;
  }

  /**
   * Create a Dashboard model from a DTO
   */
  static fromDTO(dto: DashboardDTO): Dashboard {
    return new Dashboard({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      ownerId: dto.ownerId,
      isDefault: dto.isDefault,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
      widgets: dto.widgets.map(widgetDto => DashboardWidget.fromDTO(widgetDto)),
      settings: dto.settings ? DashboardSettings.fromDTO(dto.settings) : undefined
    });
  }

  /**
   * Convert this model to a DTO for API communication
   */
  toDTO(): DashboardDTO {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      ownerId: this.ownerId,
      isDefault: this.isDefault,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
      widgets: this.widgets.map(widget => widget.toDTO()),
      settings: this.settings?.toDTO()
    };
  }

  /**
   * Create a Dashboard model from component WidgetData array
   */
  static fromWidgetDataArray(widgets: WidgetData[], id: string = 'default'): Dashboard {
    return new Dashboard({
      id,
      name: 'Dashboard',
      widgets: widgets.map(widget => DashboardWidget.fromWidgetData(widget))
    });
  }

  /**
   * Convert this model to an array of WidgetData for the component
   */
  toWidgetDataArray(): WidgetData[] {
    return this.widgets.map(widget => widget.toWidgetData());
  }

  /**
   * Generate a unique ID for a new dashboard
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }
}

export class DashboardSettings {
  layout: {
    columns: number;
    margin: number;
    gridType: string;
  };
  theme: string;
  refreshInterval?: number;
  autoSave: boolean;

  constructor(data: Partial<DashboardSettings> = {}) {
    this.layout = data.layout || {
      columns: 12,
      margin: 12,
      gridType: 'fixed'
    };
    this.theme = data.theme || 'light';
    this.refreshInterval = data.refreshInterval;
    this.autoSave = data.autoSave ?? true;
  }

  /**
   * Create settings from a DTO
   */
  static fromDTO(dto: DashboardSettingsDTO): DashboardSettings {
    return new DashboardSettings({
      layout: dto.layout,
      theme: dto.theme,
      refreshInterval: dto.refreshInterval,
      autoSave: dto.autoSave
    });
  }

  /**
   * Convert to a DTO
   */
  toDTO(): DashboardSettingsDTO {
    return {
      layout: this.layout,
      theme: this.theme,
      refreshInterval: this.refreshInterval,
      autoSave: this.autoSave
    };
  }
}

export class DashboardWidget {
  id: string;
  type: string;
  title: string;
  position: GridsterItemConfig;
  settings: DashboardWidgetSettings;
  dataSource?: string;
  data?: any;

  constructor(data: Partial<DashboardWidget> = {}) {
    this.id = data.id || this.generateId();
    this.type = data.type || 'table';
    this.title = data.title || 'Widget';
    this.position = data.position || { x: 0, y: 0, cols: 2, rows: 2, id: this.id };
    this.settings = data.settings || new DashboardWidgetSettings({
      category: this.type,
      fields: []
    });
    this.dataSource = data.dataSource;
    this.data = data.data || {};
  }

  /**
   * Create a widget from a DTO
   */
  static fromDTO(dto: DashboardWidgetDTO): DashboardWidget {
    return new DashboardWidget({
      id: dto.id,
      type: dto.type,
      title: dto.title,
      position: dto.position,
      settings: DashboardWidgetSettings.fromDTO(dto.settings),
      dataSource: dto.dataSource,
      data: dto.data
    });
  }

  /**
   * Convert to a DTO
   */
  toDTO(): DashboardWidgetDTO {
    return {
      id: this.id,
      type: this.type,
      title: this.title,
      position: this.position,
      settings: this.settings.toDTO(),
      dataSource: this.dataSource,
      data: this.data
    };
  }

  /**
   * Create a widget from component WidgetData
   */
  static fromWidgetData(widgetData: WidgetData): DashboardWidget {
    return new DashboardWidget({
      id: widgetData.id,
      type: widgetData.type,
      title: widgetData.title,
      position: widgetData.position,
      settings: widgetData.settings ? 
        DashboardWidgetSettings.fromWidgetSettings(widgetData.settings) : 
        new DashboardWidgetSettings({ category: widgetData.type }),
      dataSource: widgetData.dataSource,
      data: DashboardWidget.normalizeWidgetData(widgetData)
    });
  }

  /**
   * Convert to component WidgetData
   */
  toWidgetData(): WidgetData {
    return {
      id: this.id,
      type: this.type,
      title: this.title,
      position: this.position,
      settings: this.settings.toWidgetSettings(),
      dataSource: this.dataSource,
      data: this.denormalizeWidgetData()
    };
  }

  /**
   * Normalize date fields from UI objects to ISO strings
   */
  private static normalizeWidgetData(widget: WidgetData): any {
    const data = { ...widget.data };
    
    switch (widget.type) {
      case 'upcomingExercises':
        if (data?.exercises) {
          data.exercises = data.exercises.map((ex: any) => ({
            ...ex,
            date: ex.date instanceof Date ? ex.date.toISOString() : ex.date
          }));
        }
        break;
      
      case 'reminders':
        if (data?.reminders) {
          data.reminders = data.reminders.map((rem: any) => ({
            ...rem,
            dueDate: rem.dueDate instanceof Date ? rem.dueDate.toISOString() : rem.dueDate
          }));
        }
        break;
    }

    return data;
  }

  /**
   * Convert ISO date strings to Date objects for the UI
   */
  private denormalizeWidgetData(): any {
    const data = { ...this.data };
    
    switch (this.type) {
      case 'upcomingExercises':
        if (data?.exercises) {
          data.exercises = data.exercises.map((ex: any) => ({
            ...ex,
            date: typeof ex.date === 'string' ? new Date(ex.date) : ex.date
          }));
        }
        break;
      
      case 'reminders':
        if (data?.reminders) {
          data.reminders = data.reminders.map((rem: any) => ({
            ...rem,
            dueDate: typeof rem.dueDate === 'string' ? new Date(rem.dueDate) : rem.dueDate
          }));
        }
        break;
    }

    return data;
  }

  /**
   * Generate a unique ID for a new widget
   */
  private generateId(): string {
    return 'widget-' + Date.now().toString(36) + Math.random().toString(36).substring(2);
  }
}

export class DashboardWidgetSettings {
  category: string;
  filters: Record<string, any>;
  fields: string[];
  displayType: string;
  visualizationSettings?: any;

  constructor(data: Partial<DashboardWidgetSettings> = {}) {
    this.category = data.category || 'table';
    this.filters = data.filters || {};
    this.fields = data.fields || [];
    this.displayType = data.displayType || 'table';
    this.visualizationSettings = data.visualizationSettings;
  }

  /**
   * Create settings from a DTO
   */
  static fromDTO(dto: WidgetSettingsDTO): DashboardWidgetSettings {
    return new DashboardWidgetSettings({
      category: dto.category,
      filters: dto.filters,
      fields: dto.fields,
      displayType: dto.displayType,
      visualizationSettings: dto.visualizationSettings
    });
  }

  /**
   * Convert to a DTO
   */
  toDTO(): WidgetSettingsDTO {
    return {
      category: this.category,
      filters: this.filters,
      fields: this.fields,
      displayType: this.displayType,
      visualizationSettings: this.visualizationSettings
    };
  }

  /**
   * Create settings from component WidgetSettings
   */
  static fromWidgetSettings(settings: WidgetSettings): DashboardWidgetSettings {
    return new DashboardWidgetSettings({
      category: settings.category,
      filters: settings.filters,
      fields: settings.fields,
      displayType: settings.displayType
    });
  }

  /**
   * Convert to component WidgetSettings
   */
  toWidgetSettings(): WidgetSettings {
    return {
      category: this.category,
      filters: this.filters,
      fields: this.fields,
      displayType: this.displayType
    };
  }
} 